import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useJsApiLoader, GoogleMap, Marker } from '@react-google-maps/api';
import './MapLocationPicker.css';
import { geocodeAddress, reverseGeocode, searchAddresses, getPlaceDetails } from '../services/geocoding';

// Libraries to load with Google Maps (must be consistent across all components)
const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry'];

function useGoogleMaps() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: 'he',
    region: 'IL'
  });

  return { isLoaded, loadError, apiKey };
}

// Default center on Israel
const ISRAEL_CENTER = { lat: 32.0853, lng: 34.7818 };

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px'
};

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  language: 'he',
  region: 'il'
};

// Map Modal rendered via Portal to avoid React DOM conflicts
function MapModal({ isOpen, onClose, onConfirm, initialLocation, isGoogleLoaded }) {
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [isLocating, setIsLocating] = useState(false);
  const [mapCenter, setMapCenter] = useState(
    initialLocation ? { lat: initialLocation.lat, lng: initialLocation.lng } : ISRAEL_CENTER
  );
  const [mapZoom, setMapZoom] = useState(initialLocation ? 15 : 8);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (initialLocation) {
      setSelectedLocation(initialLocation);
      setMapCenter({ lat: initialLocation.lat, lng: initialLocation.lng });
      setMapZoom(15);
    }
  }, [initialLocation]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const onMapClick = useCallback(async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    const location = await reverseGeocode(lat, lng);
    if (location) {
      setSelectedLocation({
        ...location,
        lat,
        lng
      });
    } else {
      // Fallback if reverse geocoding fails
      setSelectedLocation({
        address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        lat,
        lng,
        name: `${lat.toFixed(6)}, ${lng.toFixed(6)}`
      });
    }
  }, []);

  const onMarkerDragEnd = useCallback(async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    const location = await reverseGeocode(lat, lng);
    if (location) {
      setSelectedLocation({
        ...location,
        lat,
        lng
      });
    }
  }, []);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('הדפדפן שלך לא תומך באיתור מיקום');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        const location = await reverseGeocode(latitude, longitude);
        
        if (location) {
          setSelectedLocation({
            ...location,
            lat: latitude,
            lng: longitude
          });
        } else {
          setSelectedLocation({
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            lat: latitude,
            lng: longitude,
            name: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          });
        }
        
        setMapCenter({ lat: latitude, lng: longitude });
        setMapZoom(15);
        setIsLocating(false);
      },
      (error) => {
        alert('לא הצלחנו לאתר את המיקום שלך. אנא ודא שנתת הרשאה לגישה למיקום.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (!isOpen) return null;

  if (!isGoogleLoaded) {
    return ReactDOM.createPortal(
      <div className="map-modal-overlay" onClick={onClose}>
        <div className="map-modal" onClick={(e) => e.stopPropagation()}>
          <div className="map-modal-header">
            <h3>טוען מפה...</h3>
            <button className="close-btn" onClick={onClose}>×</button>
          </div>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div className="btn-spinner" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
            <p style={{ marginTop: '16px' }}>טוען את Google Maps...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const modalContent = (
    <div className="map-modal-overlay" onClick={onClose}>
      <div className="map-modal" onClick={(e) => e.stopPropagation()}>
        <div className="map-modal-header">
          <h3>בחר מיקום במפה</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={mapCenter}
          zoom={mapZoom}
          onLoad={onMapLoad}
          onClick={onMapClick}
          options={mapOptions}
        >
          {selectedLocation && typeof selectedLocation.lat === 'number' && typeof selectedLocation.lng === 'number' && !isNaN(selectedLocation.lat) && !isNaN(selectedLocation.lng) && (
            <Marker
              position={{ lat: selectedLocation.lat, lng: selectedLocation.lng }}
              draggable
              onDragEnd={onMarkerDragEnd}
              ref={markerRef}
            />
          )}
        </GoogleMap>
        
        <div className="map-modal-info">
          {selectedLocation ? (
            <>
              <div className="selected-place">
                <strong>{selectedLocation.name || selectedLocation.address}</strong>
                {selectedLocation.name !== selectedLocation.address && (
                  <span>{selectedLocation.address}</span>
                )}
              </div>
              <div className="selected-coords">
                {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
              </div>
            </>
          ) : (
            <p className="map-hint">לחץ על המפה לבחירת מיקום</p>
          )}
        </div>

        <div className="map-modal-actions">
          <button 
            type="button"
            className="btn btn-secondary"
            onClick={getCurrentLocation}
            disabled={isLocating}
          >
            {isLocating ? 'מאתר...' : '📍 המיקום שלי'}
          </button>
          <button 
            type="button"
            className="btn btn-primary"
            onClick={() => selectedLocation && onConfirm(selectedLocation)}
            disabled={!selectedLocation}
          >
            אישור בחירה
          </button>
        </div>
      </div>
    </div>
  );

  // Render via portal to avoid React DOM conflicts
  return ReactDOM.createPortal(modalContent, document.body);
}

// Address input with autocomplete using Google Places
function AddressInput({ value, onChange, onLocationSelect, placeholder, disabled, isGoogleLoaded }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const skipSearchRef = useRef(false); // Use ref to track if we should skip search
  const userTypingRef = useRef(false); // Track if user is actively typing

  // Sync value from parent - but mark that we shouldn't search
  useEffect(() => {
    if (value !== query) {
      // Value changed from parent, not from user typing
      if (!userTypingRef.current) {
        skipSearchRef.current = true;
      }
      setQuery(value || '');
    }
  }, [value, query]);

  // Debounced search - only search when user is typing
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Skip search if flag is set (selection or external value change)
    if (skipSearchRef.current) {
      skipSearchRef.current = false; // Reset for next time
      return;
    }

    // Only search if user is actively typing
    if (!userTypingRef.current) {
      return;
    }

    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    // Don't search if Google Maps isn't loaded yet
    if (!isGoogleLoaded) {
      console.log('Google Maps not loaded yet, waiting...');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await searchAddresses(query);
        console.log('Autocomplete results for "' + query + '":', results);
        setSuggestions(results);
        if (results.length > 0) {
          setShowSuggestions(true);
          setSearchError(null);
        } else {
          setShowSuggestions(false);
          // Don't show error if query is too short or no results (normal case)
          if (query.trim().length >= 3) {
            setSearchError('לא נמצאו תוצאות. נסה חיפוש אחר.');
          }
        }
      } catch (error) {
        console.error('Search error:', error);
        setSuggestions([]);
        setShowSuggestions(false);
        setSearchError('שגיאה בחיפוש. אנא נסה שוב.');
      } finally {
        setIsSearching(false);
        userTypingRef.current = false; // Reset after search completes
      }
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query, isGoogleLoaded]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    userTypingRef.current = true; // Mark that user is typing
    skipSearchRef.current = false; // Don't skip search for user input
    setQuery(newValue);
    onChange?.(newValue);
  };

  const handleSelectSuggestion = async (suggestion) => {
    // Set flags to prevent search from triggering
    skipSearchRef.current = true;
    userTypingRef.current = false;
    setQuery(suggestion.address);
    setShowSuggestions(false);
    setSuggestions([]); // Clear suggestions immediately
    onChange?.(suggestion.address);
    
    try {
      // If suggestion has place_id, get full details
      if (suggestion.place_id) {
        console.log('Getting place details for:', suggestion.place_id);
        const details = await getPlaceDetails(suggestion.place_id);
        console.log('Place details result:', details);
        if (details) {
          // Set skip flag again before calling onLocationSelect
          // because it might update the value prop
          skipSearchRef.current = true;
          onLocationSelect?.(details);
        } else {
          // Fallback: use suggestion data if we have lat/lng
          skipSearchRef.current = true;
          if (suggestion.lat && suggestion.lng) {
            onLocationSelect?.(suggestion);
          } else {
            console.warn('No details and no coordinates in suggestion');
            onLocationSelect?.({ address: suggestion.address, lat: null, lng: null });
          }
        }
      } else if (suggestion.lat && suggestion.lng) {
        // Use existing coordinates
        skipSearchRef.current = true;
        onLocationSelect?.(suggestion);
      } else {
        // No place_id and no coordinates
        skipSearchRef.current = true;
        onLocationSelect?.({ address: suggestion.address, lat: null, lng: null });
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      // Fallback to suggestion data
      skipSearchRef.current = true;
      onLocationSelect?.(suggestion);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.trim().length >= 3) {
      // Try to geocode the entered address
      geocodeAddress(query).then(result => {
        if (result) {
          onLocationSelect?.(result);
        }
      });
      setShowSuggestions(false);
    }
  };

  // Get dropdown position - calculate on each render for accuracy
  const getDropdownStyle = () => {
    if (!inputRef.current) return null;
    const rect = inputRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      zIndex: 99999,
      maxHeight: '300px',
      overflowY: 'auto',
      background: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      border: '1px solid #e5e7eb'
    };
  };

  // Render suggestions dropdown via Portal to escape overflow:hidden
  const suggestionsDropdown = showSuggestions && suggestions.length > 0 && inputRef.current && ReactDOM.createPortal(
    <div 
      ref={suggestionsRef}
      className="address-suggestions-portal"
      style={getDropdownStyle()}
    >
      {suggestions.map((suggestion, idx) => (
        <div
          key={idx}
          onClick={() => handleSelectSuggestion(suggestion)}
          style={{
            padding: '12px 14px',
            cursor: 'pointer',
            borderBottom: idx < suggestions.length - 1 ? '1px solid #e5e7eb' : 'none',
            transition: 'background-color 0.15s',
            backgroundColor: 'transparent'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <div style={{ fontWeight: 500, color: '#1a1a1a', fontSize: '14px' }}>
            {suggestion.address}
          </div>
        </div>
      ))}
    </div>,
    document.body
  );

  return (
    <div className="address-input-container" style={{ position: 'relative', flex: 1 }}>
      <input
        ref={inputRef}
        type="text"
        className="form-input location-input"
        placeholder={placeholder || 'הקלד כתובת...'}
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.trim().length >= 3 && suggestions.length > 0 && setShowSuggestions(true)}
        disabled={disabled}
        autoComplete="off"
      />
      {isSearching && (
        <div className="search-spinner" style={{ 
          position: 'absolute', 
          right: '10px', 
          top: '50%', 
          transform: 'translateY(-50%)',
          width: '16px',
          height: '16px',
          border: '2px solid #e5e7eb',
          borderTop: '2px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite'
        }}></div>
      )}
      {searchError && query.trim().length >= 3 && !isSearching && (
        <div 
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            padding: '12px',
            background: 'var(--bg-surface, #fff)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            color: 'var(--text-muted, #666)',
            fontSize: '13px',
            zIndex: 99999
          }}
        >
          {searchError}
        </div>
      )}
      {suggestionsDropdown}
    </div>
  );
}

function MapLocationPicker({ 
  value = '', 
  onChange, 
  onLocationSelect,
  placeholder = 'הקלד כתובת...',
  showMap = true,
  compact = false,
  disabled = false 
}) {
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  
  // Load Google Maps API globally
  const { isLoaded: isGoogleLoaded, loadError } = useGoogleMaps();

  // Handle location selection from autocomplete
  const handleLocationSelect = useCallback((location) => {
    setSelectedLocation(location);
    onLocationSelect?.(location);
  }, [onLocationSelect]);

  // Get user's current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      alert('הדפדפן שלך לא תומך באיתור מיקום');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Reverse geocode to get address
        const location = await reverseGeocode(latitude, longitude);
        
        if (location) {
          setSelectedLocation(location);
          onChange?.(location.address);
          onLocationSelect?.(location);
        } else {
          const fallbackLocation = {
            address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
            lat: latitude,
            lng: longitude,
            name: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
          };
          setSelectedLocation(fallbackLocation);
          onChange?.(fallbackLocation.address);
          onLocationSelect?.(fallbackLocation);
        }
        
        setIsLocating(false);
      },
      (error) => {
        alert('לא הצלחנו לאתר את המיקום שלך. אנא ודא שנתת הרשאה לגישה למיקום.');
        setIsLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [onChange, onLocationSelect]);

  // Handle map modal confirmation
  const handleMapConfirm = useCallback((location) => {
    setSelectedLocation(location);
    onChange?.(location.address);
    onLocationSelect?.(location);
    setIsMapOpen(false);
  }, [onChange, onLocationSelect]);

  // Show error if Google Maps failed to load
  if (loadError) {
    console.error('Google Maps load error:', loadError);
  }

  return (
    <div className={`map-location-picker ${compact ? 'compact' : ''}`}>
      <div className="location-input-wrapper">
        <AddressInput
          value={value}
          onChange={onChange}
          onLocationSelect={handleLocationSelect}
          placeholder={placeholder}
          disabled={disabled}
          isGoogleLoaded={isGoogleLoaded}
        />
        <div className="location-buttons">
          <button
            type="button"
            className="location-btn locate-btn"
            onClick={getCurrentLocation}
            disabled={disabled || isLocating}
            title="המיקום שלי"
          >
            {isLocating ? (
              <span className="btn-spinner"></span>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
              </svg>
            )}
          </button>
          {showMap && (
            <button
              type="button"
              className="location-btn map-btn"
              onClick={() => setIsMapOpen(true)}
              disabled={disabled}
              title="בחר במפה"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {selectedLocation && (
        <div className="selected-location-info">
          <span className="location-name">{selectedLocation.name || selectedLocation.address}</span>
          {selectedLocation.name !== selectedLocation.address && (
            <span className="location-address">{selectedLocation.address}</span>
          )}
        </div>
      )}

      {/* Map Modal - rendered via portal */}
      <MapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        onConfirm={handleMapConfirm}
        initialLocation={selectedLocation}
        isGoogleLoaded={isGoogleLoaded}
      />
    </div>
  );
}

export default MapLocationPicker;
